import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Space,
  Typography,
  Tooltip,
  Modal,
  message,
  Divider,
  Form,
  Input,
  theme,
} from 'antd';
import {
  LogoutOutlined,
  UserOutlined,
  CopyOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  BellOutlined,
  DownOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import userApi from '../../api/user';

dayjs.locale('vi');

const { Text } = Typography;

function SH_header_admin() {
  const { token } = theme.useToken();

  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw || raw === 'undefined' || raw === 'null') return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const email = user?.email || 'ADMIN';

  const initials = useMemo(() => {
    const base = (email || 'A').split('@')[0];
    return base
      .split(/[.\-_ ]/g)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s?.[0]?.toUpperCase())
      .join('');
  }, [email]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [now, setNow] = useState(dayjs());

  const [notiCount] = useState(0);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwForm] = Form.useForm();

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);

    const t = setInterval(() => setNow(dayjs()), 1000);

    return () => {
      clearInterval(t);
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, []);

  const weekdayTitle = useMemo(() => {
    const w = now.format('dddd');
    return w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
  }, [now]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      message.warning('Trình duyệt không hỗ trợ fullscreen.');
    }
  }, []);

  const copyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(email);
      message.success('Đã copy email');
    } catch {
      Modal.info({
        title: 'Email',
        content: (
          <div style={{ wordBreak: 'break-all' }}>
            <Text copyable>{email}</Text>
          </div>
        ),
      });
    }
  }, [email]);

  const hardLogout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }, []);

  const logout = useCallback(() => {
    Modal.confirm({
      title: 'Đăng xuất',
      content: 'Bạn chắc chắn muốn đăng xuất?',
      okText: 'Đăng xuất',
      cancelText: 'Huỷ',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          hardLogout();
        } catch (error) {
          console.error('Có lỗi khi đăng xuất:', error);
          message.error('Đăng xuất thất bại');
        }
      },
    });
  }, [hardLogout]);

  const openChangePassword = useCallback(() => {
    pwForm.resetFields();
    setPwOpen(true);
  }, [pwForm]);

  const closeChangePassword = useCallback(() => {
    setPwOpen(false);
    pwForm.resetFields();
  }, [pwForm]);

  const onSubmitChangePassword = useCallback(
    async (values) => {
      try {
        setPwLoading(true);
        await userApi.changePassword({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        });
        message.success('Đổi mật khẩu thành công. Vui lòng đăng nhập lại.');
        closeChangePassword();
        hardLogout();
      } catch (err) {
        console.error(err);
        message.error(err?.response?.data?.message || 'Đổi mật khẩu thất bại');
      } finally {
        setPwLoading(false);
      }
    },
    [closeChangePassword, hardLogout]
  );

  const onMenuClick = useCallback(
    ({ key }) => {
      switch (key) {
        case 'copy':
          copyEmail();
          break;
        case 'change_password':
          openChangePassword();
          break;
        case 'fullscreen':
          toggleFullscreen();
          break;
        case 'logout':
          logout();
          break;
        default:
          break;
      }
    },
    [copyEmail, openChangePassword, toggleFullscreen, logout]
  );

  const menuItems = useMemo(
    () => [
      {
        key: 'profile',
        label: (
          <div style={{ padding: '8px 6px' }}>
            <Space align="start">
              <Avatar
                size={44}
                style={{
                  background: token.colorPrimaryBg,
                  color: token.colorPrimary,
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                {initials || <UserOutlined />}
              </Avatar>

              <div style={{ lineHeight: 1.15, minWidth: 240 }}>
                <div style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      maxWidth: 240,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                    }}
                    title={email}
                  >
                    {email}
                  </span>
                </div>

                <Text type="secondary" style={{ fontSize: 12 }}>
                  Quản trị hệ thống
                </Text>

                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {weekdayTitle}, {now.format('HH:mm:ss')} • {now.format('DD/MM/YYYY')}
                  </Text>
                </div>
              </div>
            </Space>
          </div>
        ),
        disabled: true,
      },
      { type: 'divider' },
      { key: 'copy', icon: <CopyOutlined />, label: 'Copy email' },
      { key: 'change_password', icon: <KeyOutlined />, label: 'Đổi mật khẩu' },
      {
        key: 'fullscreen',
        icon: isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />,
        label: isFullscreen ? 'Thoát fullscreen' : 'Fullscreen',
      },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, danger: true, label: 'Đăng xuất' },
    ],
    [email, initials, isFullscreen, now, token, weekdayTitle]
  );

  // Styles
  const headerWrapStyle = useMemo(
    () => ({
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    }),
    []
  );

  const iconBtnStyle = useMemo(
    () => ({
      width: 40,
      height: 40,
      borderRadius: 12,
      color: '#fff',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.14)',
      backdropFilter: 'blur(8px)',
      transition: 'transform 120ms ease, background 120ms ease',
    }),
    []
  );

  const userBtnStyle = useMemo(
    () => ({
      height: 44,
      borderRadius: 14,
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      paddingInline: 10,
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.14)',
      backdropFilter: 'blur(8px)',
      transition: 'transform 120ms ease, background 120ms ease',
    }),
    []
  );

  const hoverableBtnHandlers = useMemo(
    () => ({
      onMouseDown: (e) => {
        e.currentTarget.style.transform = 'scale(0.98)';
      },
      onMouseUp: (e) => {
        e.currentTarget.style.transform = 'scale(1)';
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.transform = 'scale(1)';
      },
      onMouseEnter: (e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
      },
    }),
    []
  );

  return (
    <>
      <div style={headerWrapStyle}>
        {/* Left */}
        <Space align="center" size={12}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text style={{ color: '#fff', fontWeight: 900, fontSize: 14, letterSpacing: 0.2 }}>
              SunHairCompany Admin
            </Text>

            <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: 12 }}>
              {weekdayTitle} • {now.format('HH:mm:ss')} • {now.format('DD/MM/YYYY')}
            </Text>
          </div>
        </Space>

        {/* Right */}
        <Space align="center" size={10}>
          <Tooltip title="Thông báo">
            <Badge count={notiCount} size="small" overflowCount={99}>
              <Button
                aria-label="Thông báo"
                shape="circle"
                type="text"
                icon={<BellOutlined style={{ color: '#fff' }} />}
                onClick={() => message.info('Chưa có thông báo')}
                style={iconBtnStyle}
                {...hoverableBtnHandlers}
              />
            </Badge>
          </Tooltip>

          <Tooltip title={isFullscreen ? 'Thoát fullscreen' : 'Fullscreen'}>
            <Button
              aria-label="Fullscreen"
              shape="circle"
              type="text"
              icon={
                isFullscreen ? (
                  <FullscreenExitOutlined style={{ color: '#fff' }} />
                ) : (
                  <FullscreenOutlined style={{ color: '#fff' }} />
                )
              }
              onClick={toggleFullscreen}
              style={iconBtnStyle}
              {...hoverableBtnHandlers}
            />
          </Tooltip>

          <Divider type="vertical" style={{ height: 24, borderColor: 'rgba(255,255,255,0.20)' }} />

          <Dropdown
            menu={{ items: menuItems, onClick: onMenuClick }}
            trigger={['click']}
            placement="bottomRight"
            getPopupContainer={(triggerNode) => triggerNode?.parentElement || document.body}
          >
            <Button
              type="text"
              style={userBtnStyle}
              aria-label="Tài khoản"
              {...hoverableBtnHandlers}
            >
              <Avatar size={32} style={{ background: 'rgba(255,255,255,0.14)', color: '#fff' }}>
                {initials || <UserOutlined />}
              </Avatar>

              <Tooltip title={email}>
                <span
                  style={{
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {email}
                </span>
              </Tooltip>

              <DownOutlined style={{ fontSize: 12, opacity: 0.8 }} />
            </Button>
          </Dropdown>
        </Space>
      </div>

      <Modal
        open={pwOpen}
        title="Đổi mật khẩu"
        okText="Cập nhật"
        cancelText="Huỷ"
        confirmLoading={pwLoading}
        onOk={() => pwForm.submit()}
        onCancel={closeChangePassword}
        destroyOnHidden
      >
        <div style={{ marginBottom: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Gợi ý: mật khẩu nên có chữ hoa, chữ thường, số/ký tự đặc biệt để tăng bảo mật.
          </Text>
        </div>

        <Form form={pwForm} layout="vertical" onFinish={onSubmitChangePassword}>
          <Form.Item
            label="Mật khẩu cũ"
            name="oldPassword"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu cũ' }]}
          >
            <Input.Password autoComplete="current-password" placeholder="Nhập mật khẩu cũ" />
          </Form.Item>

          <Form.Item
            label="Mật khẩu mới"
            name="newPassword"
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu mới' },
              { min: 6, message: 'Tối thiểu 6 ký tự' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                message: 'Nên có ít nhất: 1 chữ thường, 1 chữ hoa, 1 số',
              },
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="Nhập mật khẩu mới" />
          </Form.Item>

          <Form.Item
            label="Xác nhận mật khẩu mới"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Vui lòng xác nhận mật khẩu' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Xác nhận mật khẩu không khớp'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="Nhập lại mật khẩu mới" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default SH_header_admin;
